/**
 * PitchPredictionAnalysis Page
 *
 * Scouting-focused analysis page for pitch prediction patterns.
 * Shows pattern recognition, biomechanics breakdown, and video thumbnails.
 *
 * @module pages/PitchPredictionAnalysis
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Target,
  Activity,
  Film,
  Image,
  Table,
  RefreshCw,
  AlertCircle,
  Loader2,
  Play,
  ChevronRight,
  Eye,
  Download
} from 'lucide-react';
import { PatternTable, ThumbnailFilters, BiomechanicsBreakdown } from '../components/prediction';
import {
  fetchPitcherPrediction,
  fetchPitcherPatterns,
  fetchBiomechanics,
  fetchThumbnails,
  filterThumbnails,
  extractFilterOptions,
  PITCH_TYPE_LABELS,
  CONFIDENCE_LEVEL_COLORS
} from '../lib/predictionService';

// ═══════════════════════════════════════════════════════════════════════════
// TAB CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const TABS = [
  { id: 'patterns', label: 'Patterns', icon: Table },
  { id: 'thumbnails', label: 'Video Analysis', icon: Film },
  { id: 'biomechanics', label: 'Biomechanics', icon: Activity }
];

// ═══════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tab navigation component
 */
function TabNav({ activeTab, onTabChange }) {
  return (
    <div className="flex gap-1 bg-gray-100 dark:bg-white/5 p-1 rounded-lg">
      {TABS.map(tab => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
              transition-all duration-200
              ${isActive
                ? 'bg-orange-500 text-white'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
              }
            `}
            aria-selected={isActive}
            role="tab"
          >
            <Icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Summary card displaying key metrics
 */
function SummaryCard({ icon: Icon, label, value, subvalue, color = 'orange' }) {
  const colorClasses = {
    orange: 'text-orange-400',
    red: 'text-red-400',
    green: 'text-green-400',
    blue: 'text-blue-400'
  };

  return (
    <div className="bg-white dark:bg-black border border-gray-200 dark:border-white/[0.08] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${colorClasses[color]}`} />
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className={`text-2xl font-bold ${colorClasses[color]}`}>{value}</div>
      {subvalue && (
        <div className="text-xs text-gray-500 mt-1">{subvalue}</div>
      )}
    </div>
  );
}

/**
 * Thumbnail grid for video analysis
 */
function ThumbnailGrid({ thumbnails, onThumbnailClick, selectedThumbnail }) {
  if (thumbnails.length === 0) {
    return (
      <div className="bg-white dark:bg-black border border-gray-200 dark:border-white/[0.08] rounded-xl p-8 text-center">
        <Image className="w-12 h-12 mx-auto text-gray-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Thumbnails Found</h3>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Adjust your filters or analyze more videos.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {thumbnails.map((thumb, idx) => {
        const isSelected = selectedThumbnail?.videoUid === thumb.videoUid;
        const confidenceColors = CONFIDENCE_LEVEL_COLORS[thumb.confidenceLevel] || CONFIDENCE_LEVEL_COLORS.LOW;

        return (
          <button
            key={thumb.videoUid || idx}
            onClick={() => onThumbnailClick(thumb)}
            className={`
              group relative bg-white dark:bg-black border rounded-xl overflow-hidden
              transition-all duration-200 text-left
              ${isSelected
                ? 'border-orange-500 ring-2 ring-orange-500/20'
                : 'border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20'
              }
            `}
          >
            {/* Thumbnail image or placeholder */}
            <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-800 dark:to-gray-900 relative">
              {thumb.thumbnailUri ? (
                <img
                  src={thumb.thumbnailUri}
                  alt={`${thumb.pitchType} pitch`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Film className="w-8 h-8 text-gray-600" />
                </div>
              )}

              {/* Play overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                <Play className="w-10 h-10 text-white" />
              </div>

              {/* Pitch type badge */}
              <div className="absolute top-2 left-2">
                <span className={`px-2 py-1 rounded text-xs font-bold ${confidenceColors.bg} ${confidenceColors.text}`}>
                  {PITCH_TYPE_LABELS[thumb.pitchType] || thumb.pitchType}
                </span>
              </div>

              {/* Tipping indicator */}
              {thumb.tippingDetected && (
                <div className="absolute top-2 right-2">
                  <Eye className="w-4 h-4 text-orange-400" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {thumb.confidenceLevel}
                </span>
                <span className="text-xs text-gray-500">
                  {(thumb.confidence * 100).toFixed(0)}%
                </span>
              </div>
              {thumb.tells?.length > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {thumb.tells.slice(0, 2).join(', ')}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Video viewer modal/panel
 */
function VideoViewer({ thumbnail, onClose }) {
  if (!thumbnail) return null;

  return (
    <div className="bg-white dark:bg-black border border-gray-200 dark:border-white/[0.08] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-white/[0.08]">
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-lg text-sm font-bold ${CONFIDENCE_LEVEL_COLORS[thumbnail.confidenceLevel]?.bg || ''} ${CONFIDENCE_LEVEL_COLORS[thumbnail.confidenceLevel]?.text || ''}`}>
            {PITCH_TYPE_LABELS[thumbnail.pitchType] || thumbnail.pitchType}
          </span>
          <span className="text-gray-900 dark:text-white font-medium">
            {thumbnail.confidenceLevel} Confidence
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Video placeholder */}
      <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Video: {thumbnail.videoUid}</p>
          {thumbnail.gcsUri && (
            <p className="text-xs text-gray-600 mt-1 font-mono">{thumbnail.gcsUri}</p>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="p-4 space-y-3">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
            Detected Tells
          </h4>
          <div className="flex flex-wrap gap-2">
            {thumbnail.tells?.map((tell, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-gray-100 dark:bg-white/10 rounded text-xs font-medium text-gray-900 dark:text-white"
              >
                {tell.replace(/_/g, ' ')}
              </span>
            )) || (
              <span className="text-gray-500 text-sm">No tells detected</span>
            )}
          </div>
        </div>

        {thumbnail.gameDate && (
          <div className="text-xs text-gray-500">
            Game Date: {thumbnail.gameDate}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function PitchPredictionAnalysis() {
  const navigate = useNavigate();
  const { pitcherId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'patterns');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state
  const [overview, setOverview] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [signatures, setSignatures] = useState({});
  const [thumbnails, setThumbnails] = useState([]);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    pitchTypes: [],
    confidenceLevels: [],
    tellTypes: [],
    tippingOnly: false
  });

  // Compute filter options from thumbnails
  const filterOptions = useMemo(() => {
    return extractFilterOptions(thumbnails);
  }, [thumbnails]);

  // Filter thumbnails client-side
  const filteredThumbnails = useMemo(() => {
    return filterThumbnails(thumbnails, filters);
  }, [thumbnails, filters]);

  // Load data on mount
  useEffect(() => {
    if (!pitcherId) return;

    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        // Fetch all data in parallel
        const [overviewData, patternsData, biomechanicsData, thumbnailsData] = await Promise.all([
          fetchPitcherPrediction(pitcherId),
          fetchPitcherPatterns(pitcherId),
          fetchBiomechanics(pitcherId),
          fetchThumbnails(pitcherId, { limit: 100 })
        ]);

        setOverview(overviewData);
        setPatterns(patternsData.patterns || []);
        setSignatures(biomechanicsData.signatures || {});
        setThumbnails(thumbnailsData.thumbnails || []);

      } catch (err) {
        console.error('[PREDICTION_ANALYSIS] Error loading data:', err);
        setError(err.message || 'Failed to load prediction data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [pitcherId]);

  // Update URL when tab changes
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  }, [setSearchParams]);

  // Handle pattern click - filter thumbnails to that pattern
  const handlePatternClick = useCallback((pattern) => {
    setSelectedPattern(pattern.tell_type);
    setFilters(prev => ({
      ...prev,
      tellTypes: [pattern.tell_type]
    }));
    setActiveTab('thumbnails');
  }, []);

  // Handle thumbnail click
  const handleThumbnailClick = useCallback((thumb) => {
    setSelectedThumbnail(thumb);
  }, []);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setFilters({
      pitchTypes: [],
      confidenceLevels: [],
      tellTypes: [],
      tippingOnly: false
    });
    setSelectedPattern(null);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Loading prediction analysis...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error Loading Data</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4 inline-block mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 dark:bg-black/90 backdrop-blur border-b border-gray-200 dark:border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-400" />
                  {overview?.pitcherName || pitcherId}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pattern Analysis & Prediction
                </p>
              </div>
            </div>

            <TabNav activeTab={activeTab} onTabChange={handleTabChange} />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            icon={Film}
            label="Analyses"
            value={overview?.summary?.totalAnalyses || 0}
            subvalue="Videos analyzed"
          />
          <SummaryCard
            icon={Eye}
            label="Tipping Rate"
            value={overview?.summary?.tippingRate || '0%'}
            subvalue={`${overview?.summary?.tippingDetected || 0} with tells`}
            color="red"
          />
          <SummaryCard
            icon={Table}
            label="Patterns"
            value={overview?.summary?.confirmedPatterns || 0}
            subvalue="Confirmed tells"
            color="blue"
          />
          <SummaryCard
            icon={Activity}
            label="Exploitability"
            value={overview?.summary?.highestExploitability || '0%'}
            subvalue={overview?.summary?.mostReliableCategory || 'N/A'}
            color="green"
          />
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Patterns Tab */}
          {activeTab === 'patterns' && (
            <PatternTable
              patterns={patterns}
              onPatternClick={handlePatternClick}
              selectedPattern={selectedPattern}
            />
          )}

          {/* Thumbnails Tab */}
          {activeTab === 'thumbnails' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Filters sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-28">
                  <ThumbnailFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    filterOptions={filterOptions}
                    onClearAll={handleClearFilters}
                  />
                </div>
              </div>

              {/* Thumbnails grid and viewer */}
              <div className="lg:col-span-3 space-y-6">
                {selectedThumbnail ? (
                  <VideoViewer
                    thumbnail={selectedThumbnail}
                    onClose={() => setSelectedThumbnail(null)}
                  />
                ) : (
                  <ThumbnailGrid
                    thumbnails={filteredThumbnails}
                    onThumbnailClick={handleThumbnailClick}
                    selectedThumbnail={selectedThumbnail}
                  />
                )}

                {/* Results count */}
                <div className="text-sm text-gray-500 text-center">
                  Showing {filteredThumbnails.length} of {thumbnails.length} thumbnails
                </div>
              </div>
            </div>
          )}

          {/* Biomechanics Tab */}
          {activeTab === 'biomechanics' && (
            <BiomechanicsBreakdown
              signatures={signatures}
              pitchTypes={overview?.pitchTypes || Object.keys(signatures)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
